import { useState } from "react";
import {
  Lock,
  Loader2,
  AlertTriangle,
  Sparkles,
  Eye,
  EyeOff,
  Check,
  Layers,
} from "lucide-react";
import { apiv2 } from "../apiv2";
import { useAuth, setUser } from "../auth";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onComplete: () => void;
}

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (v: string) => v.length >= 8 },
  { label: "One uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v: string) => /[a-z]/.test(v) },
  { label: "One digit", test: (v: string) => /\d/.test(v) },
  {
    label: "One special character",
    test: (v: string) => /[!@#$%^&*(),.?":{}|<>\-_=+[\]\\;'/`~]/.test(v),
  },
];

export default function ForcePasswordChange({ onComplete }: Props) {
  const { user } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const passwordValid = PASSWORD_RULES.every((r) => r.test(newPassword));
  const passwordsMatch =
    newPassword === confirmPassword && newPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordValid || !passwordsMatch) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await apiv2.updateUser(user.id, {
        password: newPassword,
      });
      setUser({ ...user, must_change_password: updated.must_change_password });
      onComplete();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to change password",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-500 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
      <GreenTendrils />

      {/* Header */}
      <header
        className="relative z-10 flex items-center h-12 px-6 border-b border-white/[0.04]"
        style={{
          background: "rgba(14,14,24,0.8)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center gap-2 text-brand-500">
          <Layers size={18} />
          <span className="text-sm font-black tracking-wide">
            ZTD<span className="text-white/30 font-normal">.si</span>
          </span>
        </div>
      </header>

      <section className="relative min-h-[calc(100vh-3rem)] flex items-center justify-center">
        <div className="relative z-10 w-full max-w-md px-6 animate-slide-up">
          <div
            className="glass-hero-card rounded-2xl p-8 space-y-6"
            style={{
              background: "rgba(28,28,42,0.7)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            {/* Title */}
            <div className="text-center space-y-2">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-yellow-500/30 text-yellow-400 text-xs font-medium"
                style={{ background: "rgba(234,179,8,0.08)" }}
              >
                <Sparkles size={12} />
                Password Change Required
              </div>
              <h2 className="text-xl font-bold text-white">
                Set a New Password
              </h2>
              <p className="text-sm text-white/40">
                For security, you must change your password before continuing.
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300">
                <AlertTriangle size={16} className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New password */}
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    autoFocus
                    className="w-full pl-10 pr-10 py-2.5 bg-surface-300/50 border border-white/8 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Password rules */}
              {newPassword.length > 0 && (
                <div className="grid grid-cols-2 gap-1 px-1">
                  {PASSWORD_RULES.map((rule) => (
                    <div
                      key={rule.label}
                      className={`text-[11px] flex items-center gap-1 ${rule.test(newPassword) ? "text-green-400" : "text-white/30"}`}
                    >
                      <Check
                        size={10}
                        className={
                          rule.test(newPassword)
                            ? "text-green-400"
                            : "text-white/15"
                        }
                      />
                      {rule.label}
                    </div>
                  ))}
                </div>
              )}

              {/* Confirm password */}
              <div>
                <label className="block text-xs font-medium text-white/50 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    className={`w-full pl-10 pr-4 py-2.5 bg-surface-300/50 border rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 ${
                      confirmPassword.length > 0 && !passwordsMatch
                        ? "border-red-500/50"
                        : "border-white/8"
                    }`}
                  />
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-[11px] text-red-400 mt-1">
                    Passwords do not match
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={saving || !passwordValid || !passwordsMatch}
                className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2 transition-all"
                style={{
                  background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                  boxShadow:
                    "0 0 30px rgba(134,188,37,0.3), 0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                {saving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Lock size={16} />
                )}
                Set New Password & Continue
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
